/**
 * Serialisasi tanda tangan dari canvas.
 *
 * Dipisahkan dari komponen agar dapat diuji tanpa DOM: fungsi di sini menerima apa pun yang
 * punya `toBlob`, bukan `HTMLCanvasElement` yang sesungguhnya.
 */

/** Bagian `HTMLCanvasElement` yang benar-benar dipakai — cukup untuk diuji dengan stub. */
export interface BlobCanvas {
  toBlob(callback: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

export class EmptySignatureError extends Error {
  constructor() {
    super('Tanda tangan kosong.');
    this.name = 'EmptySignatureError';
  }
}

/**
 * Canvas → PNG.
 *
 * PNG, bukan JPEG: tanda tangan adalah garis tipis di atas latar polos, dan artefak kompresi
 * JPEG membuatnya berbulu — persis pada bukti yang mungkin dipakai saat pelanggan menyanggah.
 * Server juga hanya menerima PNG untuk purpose `DELIVERY_SIGNATURE`.
 */
export function canvasToPngBlob(canvas: BlobCanvas): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob || blob.size === 0) {
        // Blob kosong berarti tidak ada yang tergambar, atau canvas gagal diserialisasi.
        // Mengunggahnya akan menghasilkan bukti bertanda tangan kosong yang lolos verifikasi
        // checksum — sah secara teknis, tidak berguna sebagai bukti.
        reject(new EmptySignatureError());
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Benar bila goresan cukup berarti untuk disebut tanda tangan.
 *
 * Satu ketukan tak sengaja menghasilkan satu titik. Menerimanya berarti menyimpan bukti kosong
 * yang tampak sah. Ambangnya sengaja rendah — paraf pendek tetap sah, yang ditolak hanyalah
 * sentuhan tunggal.
 */
export function hasMeaningfulStroke(strokes: readonly (readonly Point[])[]): boolean {
  const totalPoints = strokes.reduce((sum, stroke) => sum + stroke.length, 0);
  return totalPoints >= 4;
}
